// =============================================================================
// squelettes.js — chargements à la FORME RÉELLE du contenu attendu (lot L5,
// synthèse §6.5 et annexe « pré-vol » n° 11 ; audit mouvement u3, L3 et O8).
//
// Un « ⏳ Chargement… » centré fait sauter la page quand le contenu arrive ; un
// squelette qui a déjà la silhouette du contenu (même cartes, mêmes lignes,
// mêmes hauteurs) ne fait que se remplir. Chaque squelette est :
//   • aria-busy + un libellé pour lecteur d'écran (« Chargement des documents ») ;
//   • construit sur la classe .aramis-shimmer existante (reflet immobile en
//     mouvement réduit, section « effets » de src/feuilleGlobale.js) ;
//   • LÉGER : aucun import lourd, pour servir aussi de repli de <Suspense>
//     sans tirer le module paresseux qu'il remplace.
// =============================================================================

// Un bloc gris du squelette. Tuile = rayon 22 % (charte : la tuile, pas le rond).
function Bloc({ l = "100%", h = 12, tuile = false, rayon = 4, style }) {
  return (
    <span className="aramis-shimmer squelette-bloc" aria-hidden="true"
      style={{ width: l, height: h, borderRadius: tuile ? "22%" : rayon, ...style }} />
  );
}

function Annonce({ libelle }) {
  return <span className="squelette-sr">{libelle}</span>;
}

/** Documents d'une séance : une carte par point d'ordre du jour, avec son
 *  titre et ses lignes de pièces (icône, nom, taille et date, flèche). Forme de
 *  ThemisDocumentsSeance.js (état « ok », groupes). */
export function SqueletteDocumentsSeance({ points = 2, pieces = 3 }) {
  return (
    <div aria-busy="true" className="squelette">
      <Annonce libelle="Chargement des documents de la séance" />
      {Array.from({ length: points }).map((_, i) => (
        <div key={i} className="card" style={{ marginBottom: 12, padding: 14 }}>
          <Bloc l={i === 0 ? "42%" : "56%"} h={12} style={{ marginBottom: 12 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {Array.from({ length: pieces }).map((__, j) => (
              <div key={j} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                borderRadius: "var(--radius-sm)", border: "1px solid var(--border-color)" }}>
                <Bloc l={18} h={18} tuile />
                <span style={{ flex: 1, minWidth: 0, display: "grid", gap: 5 }}>
                  <Bloc l={`${70 - j * 12}%`} h={11} />
                  <Bloc l="28%" h={8} />
                </span>
                <Bloc l={12} h={12} rayon={3} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Fil de commentaires : avatar-tuile, nom et date, deux lignes de texte.
 *  Forme de l'ancien fil ProjetCommentaires.js (supprimé au lot L11p). */
export function SqueletteCommentaires({ nombre = 3 }) {
  return (
    <div aria-busy="true" className="squelette" style={{ display: "grid", gap: 12, padding: "10px 0" }}>
      <Annonce libelle="Chargement des commentaires" />
      {Array.from({ length: nombre }).map((_, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <Bloc l={30} h={30} tuile />
          <span style={{ flex: 1, minWidth: 0, display: "grid", gap: 6 }}>
            <Bloc l="34%" h={10} />
            <Bloc l="92%" h={9} />
            <Bloc l={`${64 - i * 10}%`} h={9} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Page ouverte depuis un lien de convocation (présence, émargement, pouvoir) :
 *  une carte, son titre, les informations de la séance, un bouton d'action. */
export function SquelettePageJeton() {
  return (
    <div aria-busy="true" className="squelette" style={{ maxWidth: 560, margin: "32px auto", padding: "0 16px" }}>
      <Annonce libelle="Chargement de la page" />
      <div className="card" style={{ padding: 20, display: "grid", gap: 10 }}>
        <Bloc l="48%" h={14} />
        <Bloc l="72%" h={10} />
        <Bloc l="60%" h={10} />
        <Bloc l="100%" h={44} rayon={12} style={{ marginTop: 10 }} />
      </div>
    </div>
  );
}

/** Liste générique de lignes (tuile, deux lignes de texte, pastille). */
export function SqueletteLignes({ nombre = 3 }) {
  return (
    <div aria-busy="true" className="squelette" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Annonce libelle="Chargement" />
      {Array.from({ length: nombre }).map((_, i) => (
        <div key={i} className="card" style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <Bloc l={36} h={36} tuile />
          <span style={{ flex: 1, minWidth: 0, display: "grid", gap: 6 }}>
            <Bloc l="45%" h={13} />
            <Bloc l="75%" h={10} />
          </span>
          <Bloc l={60} h={22} rayon={12} />
        </div>
      ))}
    </div>
  );
}

/** Liste de fichiers (bibliothèque de documents) : icône, nom, taille et date. */
export function SqueletteFichiers({ nombre = 4 }) {
  return (
    <div aria-busy="true" className="squelette" style={{ display: "flex", flexDirection: "column", gap: 4, padding: "6px 0" }}>
      <Annonce libelle="Chargement des documents" />
      {Array.from({ length: nombre }).map((_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px" }}>
          <Bloc l={18} h={18} tuile />
          <span style={{ flex: 1, minWidth: 0, display: "grid", gap: 5 }}>
            <Bloc l={`${62 - (i % 3) * 14}%`} h={10} />
            <Bloc l="22%" h={8} />
          </span>
        </div>
      ))}
    </div>
  );
}
